import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Bar, CartesianGrid, ComposedChart, Legend, Line, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { Globe, Store, Layers, User, Sparkles, Info } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PageHeader, Panel, Empty } from "@/components/PageHeader";
import { CustomerPicker } from "@/components/CustomerPicker";
import { PromoTables } from "@/components/PromoTables";
import { api, eur, num, errMsg } from "@/lib/api";

const PickerFallback = (data) => (data?.scope.type === "customer" ? { name: data.scope.name } : null);

const SCOPES = [["general", "Generale", Globe], ["store", "Punto vendita", Store], ["segment", "Segmento", Layers], ["customer", "Cliente", User]];
const PERIODS = [["1", "Ultimo mese"], ["3", "Ultimi 3 mesi"], ["6", "Ultimi 6 mesi"], ["12", "Ultimi 12 mesi"]];
const MONTHS = ["Gen", "Feb", "Mar", "Apr", "Mag", "Giu", "Lug", "Ago", "Set", "Ott", "Nov", "Dic"];
const it = (v, d = 1) => (v ?? 0).toLocaleString("it-IT", { maximumFractionDigits: d });

const Kpi = ({ label, value, hint, id }) => (
  <div className="rounded-xl border border-slate-200 bg-white p-4">
    <div className="text-[11px] uppercase tracking-wider text-slate-500">{label}</div>
    <div className="mt-1.5 font-heading text-xl font-semibold text-slate-900" data-testid={`promo-kpi-${id}`}>{value}</div>
    {hint && <div className="mt-0.5 text-[11px] text-slate-400">{hint}</div>}
  </div>
);

export default function Promotions() {
  const [params, setParams] = useSearchParams();
  const nav = useNavigate();
  const [scope, setScope] = useState(params.get("scope") || "general");
  const [scopeId, setScopeId] = useState(params.get("id") || "");
  const [customer, setCustomer] = useState(null);
  const [stores, setStores] = useState([]);
  const [segments, setSegments] = useState([]);
  const [months, setMonths] = useState(["1", "3", "6", "12"].includes(params.get("months")) ? params.get("months") : "3");
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/stores").then((r) => setStores(r.data)).catch(() => {});
    api.get("/segments").then((r) => setSegments(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (scope !== "general" && !scopeId) { setData(null); setParams({ scope, months }, { replace: true }); return; }
    let live = true;
    setLoading(true); setError(""); setData(null);
    setParams({ scope, ...(scopeId ? { id: scopeId } : {}), months }, { replace: true });
    const key = { store: "store_id", segment: "segment_id", customer: "customer_id" }[scope];
    api.get("/promo", { params: { months, ...(key ? { [key]: scopeId } : {}) } })
      .then((r) => live && setData(r.data))
      .catch((e) => live && setError(errMsg(e)))
      .finally(() => live && setLoading(false));
    return () => { live = false; };
  }, [scope, scopeId, months]); // eslint-disable-line react-hooks/exhaustive-deps

  const pick = (k) => { setScope(k); setScopeId(""); setCustomer(null); };
  const chart = (data?.monthly || []).map((m) => ({ ...m, label: `${MONTHS[parseInt(m.month.slice(5), 10) - 1]} ${m.month.slice(2, 4)}` }));

  return (
    <div>
      <PageHeader title="Impatto promozioni" subtitle="Peso, investimento ed efficacia delle iniziative promozionali">
        {data && scope !== "segment" && (
          <Button data-testid="promo-ai-button" className="bg-blue-600 hover:bg-blue-700"
            onClick={() => nav(`/analisi-ia?scope=${scope}${scopeId ? `&id=${scopeId}` : ""}&kind=promo`)}>
            <Sparkles className="mr-2 h-4 w-4" />Valuta con IA
          </Button>
        )}
      </PageHeader>

      <Panel className="mb-6" testId="promo-controls">
        <div className="grid gap-4 p-5 lg:grid-cols-[auto_1fr_200px]">
          <div className="grid grid-cols-4 gap-1 rounded-lg bg-slate-100 p-1">
            {SCOPES.map(([k, l, Icon]) => (
              <button key={k} data-testid={`promo-scope-${k}`} onClick={() => pick(k)}
                className={`flex items-center justify-center gap-1.5 rounded-md px-2 py-1.5 text-xs font-medium transition-colors sm:text-sm ${scope === k ? "bg-white text-slate-900 shadow-sm" : "text-slate-500"}`}>
                <Icon className="hidden h-4 w-4 sm:block" />{l}
              </button>
            ))}
          </div>
          {scope === "general" && <div className="flex items-center text-sm text-slate-500">Tutti i clienti fedeltà della rete</div>}
          {scope === "store" && (
            <Select value={scopeId} onValueChange={setScopeId}>
              <SelectTrigger data-testid="promo-store-select"><SelectValue placeholder="Scegli punto vendita" /></SelectTrigger>
              <SelectContent className="max-h-80 bg-white">{stores.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
            </Select>
          )}
          {scope === "segment" && (
            <Select value={scopeId} onValueChange={setScopeId}>
              <SelectTrigger data-testid="promo-segment-select"><SelectValue placeholder="Scegli segmento" /></SelectTrigger>
              <SelectContent className="bg-white">{segments.map((s) => <SelectItem key={s.id} value={s.id}>{s.name} ({num(s.count)})</SelectItem>)}</SelectContent>
            </Select>
          )}
          {scope === "customer" && <CustomerPicker testId="promo-customer" value={customer || PickerFallback(data)} onPick={(c) => { setCustomer(c); setScopeId(c.id); }} />}
          <Select value={months} onValueChange={setMonths}>
            <SelectTrigger data-testid="promo-period-select"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white">{PERIODS.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </Panel>

      {error && <div data-testid="promo-error" className="mb-6 rounded-xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700">{error}</div>}
      {scope !== "general" && !scopeId && !error && <Panel><Empty text="Seleziona l'elemento da analizzare." /></Panel>}
      {loading && <div className="grid gap-4 md:grid-cols-4">{[...Array(8)].map((_, i) => <Skeleton key={i} className="h-24" />)}<p className="text-xs text-slate-400 md:col-span-4">Analisi delle righe scontrino in corso… può richiedere fino a 20 secondi.</p></div>}

      {data && !loading && (
        <div className="animate-rise space-y-6">
          <h2 className="font-heading text-xl font-semibold text-slate-900" data-testid="promo-scope-name">
            {data.scope.name}{data.scope.capped && <span className="ml-2 text-sm font-normal text-slate-500">campione di {num(data.scope.sampled)} su {num(data.scope.members)} clienti</span>}
          </h2>
          <div className="grid grid-cols-2 gap-4 md:grid-cols-3 xl:grid-cols-6">
            <Kpi id="share" label="Quota vendite in promo" value={`${it(data.promo_share)}%`} hint={data.benchmark_promo_share != null ? `rete: ${it(data.benchmark_promo_share)}%` : `${eur(data.promo_spend)} su ${eur(data.spend)}`} />
            {data.promo_sensitivity_index != null
              ? <Kpi id="sensitivity" label="Indice sensibilità promo" value={it(data.promo_sensitivity_index, 2)} hint="1 = in linea con la rete" />
              : <Kpi id="spend" label="Vendite in promo" value={eur(data.promo_spend)} />}
            <Kpi id="discount" label="Sconto stimato" value={eur(data.discount)} hint={`profondità media ${it(data.discount_pct)}%`} />
            <Kpi id="receipts" label="Scontrini con promo" value={`${it(data.promo_receipts_share)}%`} hint={`${num(data.promo_receipts)} su ${num(data.receipts)}`} />
            <Kpi id="clients" label={data.scope.type === "customer" ? "Codici promo usati" : "Clienti raggiunti"} value={data.scope.type === "customer" ? num(data.codes.length) : num(data.promo_clients)} />
            {data.scope.type !== "customer" && <Kpi id="codes" label="Codici promo attivi" value={num(data.codes.length)} />}
            {data.converted_products != null && <Kpi id="converted" label="Prodotti convertiti" value={num(data.converted_products)} hint="riacquistati a prezzo pieno dopo la promo" />}
          </div>

          <Panel title="Vendite normali e in promozione per mese" testId="promo-monthly-chart">
            <div className="h-72 p-4">
              <ResponsiveContainer>
                <ComposedChart data={chart}>
                  <CartesianGrid stroke="#f1f5f9" vertical={false} />
                  <XAxis dataKey="label" tickLine={false} axisLine={false} fontSize={12} />
                  <YAxis yAxisId="l" tickLine={false} axisLine={false} fontSize={11} width={80} tickFormatter={(v) => num(v)} />
                  <YAxis yAxisId="r" orientation="right" tickLine={false} axisLine={false} fontSize={11} width={40} tickFormatter={(v) => `${v}%`} />
                  <Tooltip formatter={(v, n) => (n === "Quota promo" ? `${it(v)}%` : eur(v))} />
                  <Legend wrapperStyle={{ fontSize: 12 }} formatter={(v) => <span style={{ color: "#475569" }}>{v}</span>} />
                  <Bar yAxisId="l" dataKey="normal_spend" name="Prezzo pieno" stackId="a" fill="#cbd5e1" />
                  <Bar yAxisId="l" dataKey="promo_spend" name="In promo" stackId="a" fill="#2563eb" radius={[4, 4, 0, 0]} />
                  <Line yAxisId="r" dataKey="promo_share" name="Quota promo" stroke="#f59e0b" strokeWidth={2} dot={{ r: 3 }} />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          </Panel>

          <PromoTables data={data} />

          <p className="flex items-start gap-2 text-xs text-slate-400" data-testid="promo-method-note">
            <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            Sconto stimato = prezzo di listino × quantità − pagato, solo per articoli a pezzo (quelli a peso sono esclusi, quindi lo sconto è sottostimato). Uplift = variazione della quantità per riga in promo rispetto al prezzo pieno.
          </p>
        </div>
      )}
    </div>
  );
}
